/**
 * useAutoRefresh — lightweight polling hook that invokes `fn` every
 * `intervalMs` milliseconds. The user can toggle it on/off via the
 * returned `enabled` flag. Pauses automatically when the tab is hidden
 * so we don't burn quota while the user isn't looking.
 */
import { useEffect, useRef, useState, useCallback } from "react";

export function useAutoRefresh(fn, intervalMs = 30000, options = {}) {
  const { defaultEnabled = true, runImmediately = false } = options;
  const [enabled, setEnabled] = useState(defaultEnabled);
  const [lastRunAt, setLastRunAt] = useState(null);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const runNow = useCallback(async () => {
    try { await fnRef.current(); }
    catch (_e) { /* ignore — let consumer handle its own errors */ }
    finally { setLastRunAt(new Date()); }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    if (runImmediately) runNow();
    const tid = window.setInterval(() => {
      if (document.visibilityState === "visible") runNow();
    }, Math.max(5000, intervalMs));
    const onVisibility = () => {
      if (document.visibilityState === "visible") runNow();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(tid);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, intervalMs, runImmediately, runNow]);

  return { enabled, setEnabled, lastRunAt, runNow };
}

/** Compact UI toggle — shows a refresh status chip. Pairs with useAutoRefresh. */
export function AutoRefreshToggle({ enabled, setEnabled, lastRunAt, label = "Auto-refresh", intervalLabel = "30s" }) {
  const since = lastRunAt ? Math.floor((Date.now() - lastRunAt.getTime()) / 1000) : null;
  return (
    <button
      type="button"
      data-testid="auto-refresh-toggle"
      onClick={() => setEnabled(!enabled)}
      title={lastRunAt ? `Last refresh ${since}s ago` : "Toggle auto-refresh"}
      className={
        "inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-[10.5px] font-semibold border transition-colors " +
        (enabled
          ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-600"
          : "bg-gray-100 border-gray-300 text-gray-500 hover:border-gray-400")
      }
    >
      <span className={"w-1.5 h-1.5 rounded-full " + (enabled ? "bg-emerald-500 animate-pulse" : "bg-gray-400")} />
      {label} · {intervalLabel}
      {enabled && since !== null && (
        <span className="text-[9.5px] opacity-70 tabular-nums">· {since}s</span>
      )}
    </button>
  );
}
